import './App.css';
import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AuthLogin from './components/Auth/AuthLogin';
import KMZViewerGoogle from './components/KMZViewerGoogle';
import KMZViewer from './components/KMZViewer';
import Map from './components/Map';
import TestMap from './components/TestMap';
import MunicipalityAdminDashboard from './components/Dashboards/MunicipalityAdminDashboard';
import MunicipalityTechnicalDashboard from './components/Dashboards/MunicipalityTechnicalDashboard';
import MunicipalityLandscapeDashboard from './components/Dashboards/MunicipalityLandscapeDashboard';
import AdminDashboard from './components/Dashboards/AdminDashboard';
import OperatorDashboard from './components/Dashboards/OperatorDashboard';
import ExampleDashboard from './components/Dashboards/ExampleDashboard';
import AskiDashboard from './components/Dashboards/AskiDashboard';
import IndustryDashboard from './components/Dashboards/IndustryDashboard';
import AnalystDashboard from './components/Dashboards/AnalystDashboard';
import DBMode from './components/Dashboards/DBMode';
import styles from './App.module.css';

// Harita route component - Map component'ini render eder
const MapRoute = () => {
  return <Map />;
};

// Ana routing component
const AppContent = () => {
  const { isAuthenticated, userRole, logout, loading } = useAuth();
  const [currentPage, setCurrentPage] = useState(null);
  
  // Default sayfa belirleme - role göre
  const getDefaultPage = (role, isLoading, isAuth) => {
    if (isLoading || !isAuth) return null;
    
    // Role henüz yüklenmemişse bekle
    if (isAuth && role === null) {
      return null; // Role yüklenene kadar bekle
    }
    
    if (!role) return null;
    
    // Case-insensitive role kontrolü
    const roleLower = role.toLowerCase();
    
    // Municipality-Admin
    if (roleLower === 'municipality-admin') {
      return 'municipality-admin-dashboard';
    }
    
    // Municipality-Technical
    if (roleLower === 'municipality-technical') {
      return 'municipality-technical-dashboard';
    }
    
    // Municipality-Landscape
    if (roleLower === 'municipality-landscape') {
      return 'municipality-landscape-dashboard';
    }
    
    // Admin
    if (roleLower === 'admin') {
      return 'admin-dashboard';
    }
    
    // Operator
    if (roleLower === 'operator') {
      return 'operator-dashboard';
    }
    
    // Example
    if (roleLower === 'example') {
      return 'example-dashboard';
    }
    
    // ASKI
    if (roleLower === 'aski') {
      return 'aski-dashboard';
    }
    
    // Industry
    if (roleLower === 'industry') {
      return 'industry-dashboard';
    }
    
    // Analyst
    if (roleLower === 'analyst') {
      return 'analyst-dashboard';
    }
    
    // Role yoksa veya tanımsızsa, hiçbir şey gösterme
    console.warn('⚠️ Tanımsız role:', role);
    return null;
  };
  
  // Role yüklendikten sonra default sayfayı ayarla
  useEffect(() => {
    if (!loading && isAuthenticated && userRole) {
      const defaultPage = getDefaultPage(userRole, loading, isAuthenticated);
      if (defaultPage && currentPage === null) {
        setCurrentPage(defaultPage);
      }
    } else if (!loading && !isAuthenticated) {
      setCurrentPage(null);
    }
  }, [userRole, loading, isAuthenticated, currentPage]);
  
  useEffect(() => {
    console.log("🎯 App component yüklendi");
    console.log("📄 Aktif sayfa:", currentPage);
    console.log("👤 Kullanıcı rolü:", userRole);
    console.log("🔐 Authenticated:", isAuthenticated);
  }, [currentPage, userRole, isAuthenticated]);

  // Giriş yapılmamışsa sadece login sayfasını göster
  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden font-sans">
        {/* Arka Plan Dekoratif Elementler */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/20 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-cyan-500/10 rounded-full blur-[120px]"></div>
        
        <div className="relative z-10 text-center">
          {/* Modern Spinner */}
          <div className="relative mb-8 mx-auto w-20 h-20">
            <div className="absolute inset-0 border-4 border-white/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-transparent border-t-white rounded-full animate-spin"></div>
            <div className="absolute inset-2 border-4 border-white/10 rounded-full"></div>
            <div className="absolute inset-2 border-4 border-transparent border-r-cyan-300 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>
          
          {/* Modern Yazı */}
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 tracking-tight">
            <span className="bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-transparent">
              Yükleniyor
            </span>
          </h1>
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-2 h-2 bg-cyan-300 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-cyan-300 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-cyan-300 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
          <p className="text-blue-200/70 text-sm md:text-base font-light">Pronuve Su Yönetimi</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthLogin />;
  }

  // Authenticated ama role henüz yüklenmemişse bekle
  if (isAuthenticated && userRole === null && !loading) {
  return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#f8fafc',
        color: '#1e293b',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', marginBottom: '16px' }}>Kullanıcı bilgileri yükleniyor...</div>
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
            Role ayarlanmamış olabilir. Lütfen Cognito'da kullanıcıyı bir group'a ekleyin.
          </div>
        <button 
            onClick={logout} 
            style={{ 
              padding: '12px 24px', 
              background: '#2563eb', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Çıkış Yap
        </button>
        </div>
      </div>
    );
  }

  // Role yoksa veya tanımsızsa hata mesajı göster
  if (isAuthenticated && userRole !== null && !getDefaultPage(userRole, loading, isAuthenticated)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#f8fafc',
        color: '#1e293b',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <h2>Role Bulunamadı</h2>
        <p>Hesabınızda geçerli bir role tanımlanmamış. Lütfen yönetici ile iletişime geçin.</p>
        <p style={{ fontSize: '14px', color: '#64748b' }}>Mevcut rol: <strong>{userRole}</strong></p>
        <button 
          onClick={logout} 
          style={{ 
            padding: '12px 24px', 
            background: '#2563eb', 
            color: 'white', 
            border: 'none', 
            borderRadius: '6px', 
            cursor: 'pointer' 
          }}
        >
          Çıkış Yap
        </button>
      </div>
    );
  }

  // Harita sayfası mı kontrolü
  const isMapPage = currentPage === 'map';
  // ASKI dashboard kendi navbar'ına sahip
  const isAskiDashboard = currentPage === 'aski-dashboard';
  // Example dashboard için navbar gösterilmeli (SEÇİM KÜMESİ ekranında da gösterilecek)
  const isExampleDashboard = false; // Example dashboard için navbar App.js'te gösterilecek

  return (
    <div className={styles.app}>
            {!isMapPage && !isExampleDashboard && !isAskiDashboard && (
              <div className={styles.navBar}>
                <div className={styles.navBarContent}>
                  <div className={styles.navBarLeft}>
                    <img 
                      src="/logo.png" 
                      alt="Pronuve Logo" 
                      className={styles.navBarLogo}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <h3 className={styles.navBarTitle}>Pronuve Su Yönetimi</h3>
                  </div>
                  <div className={styles.navBarRight}>
                    {/* Sağ üst butonlar kaldırıldı */}
                  </div>
                </div>
              </div>
            )}
      
      <div className={styles.content}>
        {currentPage === 'municipality-admin-dashboard' ? (
          <ProtectedRoute allowedRoles={['Municipality-Admin']}>
            <MunicipalityAdminDashboard onNavigateToMap={() => window.open('/map', '_blank')} />
          </ProtectedRoute>
        ) : currentPage === 'municipality-technical-dashboard' ? (
          <ProtectedRoute allowedRoles={['Municipality-Technical']}>
            <MunicipalityTechnicalDashboard onNavigateToMap={() => window.open('/map', '_blank')} />
          </ProtectedRoute>
        ) : currentPage === 'municipality-landscape-dashboard' ? (
          <ProtectedRoute allowedRoles={['Municipality-Landscape']}>
            <MunicipalityLandscapeDashboard onNavigateToMap={() => window.open('/map', '_blank')} />
          </ProtectedRoute>
        ) : currentPage === 'admin-dashboard' ? (
          <ProtectedRoute allowedRoles={['Admin']}>
            <AdminDashboard onNavigateToMap={() => window.open('/map', '_blank')} />
          </ProtectedRoute>
        ) : currentPage === 'operator-dashboard' ? (
          <ProtectedRoute allowedRoles={['Operator']}>
            <OperatorDashboard onNavigateToMap={() => window.open('/map', '_blank')} />
          </ProtectedRoute>
        ) : currentPage === 'example-dashboard' ? (
          <ProtectedRoute allowedRoles={['Example']}>
            <ExampleDashboard />
          </ProtectedRoute>
        ) : currentPage === 'aski-dashboard' ? (
          <ProtectedRoute allowedRoles={['ASKİ', 'aski']}>
            <AskiDashboard />
          </ProtectedRoute>
        ) : currentPage === 'industry-dashboard' ? (
          <ProtectedRoute allowedRoles={['Industry', 'industry']}>
            <IndustryDashboard />
          </ProtectedRoute>
        ) : currentPage === 'analyst-dashboard' ? (
          <ProtectedRoute allowedRoles={['Analyst', 'analyst']}>
            <AnalystDashboard onNavigateToMap={() => window.open('/map', '_blank')} />
          </ProtectedRoute>
        ) : currentPage === null ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <h2>Sayfa Yükleniyor...</h2>
            <p>Lütfen bekleyin.</p>
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <h2>Sayfa Bulunamadı</h2>
            <p>Sayfa: {currentPage}</p>
            <button 
              onClick={() => {
                const defaultPage = getDefaultPage(userRole, false, isAuthenticated);
                setCurrentPage(defaultPage);
              }}
              style={{ 
                padding: '12px 24px', 
                background: '#2563eb', 
                color: 'white', 
                border: 'none', 
                borderRadius: '6px', 
                cursor: 'pointer' 
              }}
            >
              Ana Sayfaya Dön
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Ana App component - AuthProvider ve Router ile sarmalıyoruz
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Map route - ayrı sayfa olarak erişilebilir */}
          <Route path="/map" element={<MapRoute />} />
          
          {/* KMZ Viewer route - MapLibre GL */}
          <Route path="/kmz" element={<KMZViewer />} />
          
          {/* Google API ile KMZ Viewer route */}
          <Route path="/kmz-google" element={<KMZViewerGoogle />} />
          
          {/* Test Sayfası route - TestMap component'i (Park seçimi) */}
          <Route path="/test" element={<TestMap />} />
          
          {/* DB Mode - Tam ekran veritabani yonetimi */}
          <Route path="/db" element={<DBMode />} />
          
          {/* Ana uygulama route'u */}
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
